import Finder from '../../../common/helpers/finder';
import { printGuideLog, summarizePathsForLog } from '../helpers/guideLogHelper';
import { MessageTypes } from '../types/MessageTypes';
import ClickCapture from './ClickCapture';
import { isGuideRecoveryDisabled } from '../helpers/guideRecoveryHelper';
import { scrollElementIntoViewForGuide } from '../helpers/guideScrollHelper';

class Iframe {
  private static readonly RECOVERY_THROTTLE_MS = 300;

  private static instance: Iframe | null = null;
  private currentElement?: HTMLElement | null = null;
  private clickedElement?: HTMLElement | null = null;
  private frameElement?: HTMLIFrameElement | null = null;
  private positionAnimationId: number | null = null;
  private lastPosition: { x: number; y: number; width: number; height: number } | null = null;
  private lastVisibility: boolean | null = null;
  private lastRecoveryAttemptAt = 0;
  private isRecoveringElement = false;
  private paths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
    iframe?: boolean;
  }[] = [];
  private source: MessageEventSource | null = null;
  private targetOrigin: string | null = null;
  private constructor() {
    this.initialize();
  }

  public static getInstance(): Iframe {
    if (!Iframe.instance) {
      Iframe.instance = new Iframe();
    }
    return Iframe.instance;
  }

  private initialize(): void {
    this.setupListeners();
    this.checkRunningGuide();
    ClickCapture.init(this.clickEventHandler);
  }

  private checkRunningGuide(): void {
    window.parent.postMessage(
      {
        type: MessageTypes.CHECK_RUNNING_GUIDE
      },
      '*'
    );
  }

  private clickEventHandler = (e: MouseEvent) => {
    this.clickedElement = Finder.deepElementFromPoint(
      document,
      e.clientX,
      e.clientY
    ) as HTMLElement;
    this.source?.postMessage(
      {
        type: MessageTypes.GO_TO_NEXT_STEP
      },
      {
        targetOrigin: this.targetOrigin as string
      }
    );
  };

  private stopPositionMonitoring = () => {
    if (this.positionAnimationId) {
      cancelAnimationFrame(this.positionAnimationId);
      this.positionAnimationId = null;
    }
    this.lastPosition = null;
    this.lastRecoveryAttemptAt = 0;
    this.isRecoveringElement = false;
  };

  private tryRecoverLostElement = (currentTime: number) => {
    if (!this.isRecoveringElement) {
      this.isRecoveringElement = true;
      this.sendElementLost();
      printGuideLog({
        message: '[Iframe] positionMonitor:elementLost',
        data: { pathSummary: summarizePathsForLog(this.paths) },
        level: 'warn'
      });
    }

    if (isGuideRecoveryDisabled()) return;
    if (currentTime - this.lastRecoveryAttemptAt < Iframe.RECOVERY_THROTTLE_MS) return;
    this.lastRecoveryAttemptAt = currentTime;
    if (!this.paths.length) return;

    const { mainPaths } = Finder.splitPathsByIframe(this.paths);
    const found = Finder.findElementByPaths(mainPaths) as HTMLElement | null;
    if (!found || !found.isConnected) return;

    this.currentElement = found;
    this.isRecoveringElement = false;
    this.lastPosition = null;
    this.lastVisibility = null;
    printGuideLog({
      message: '[Iframe] positionMonitor:recoveredElement',
      data: { tag: found.tagName, id: found.id }
    });
    this.sendPositionChanged();
  };

  private startPositionMonitoring = () => {
    this.stopPositionMonitoring();

    let lastCheckTime = 0;
    const THROTTLE_MS = 25;
    const POSITION_THRESHOLD = 2;

    const checkPosition = (currentTime: number) => {
      if (!this.currentElement || !this.currentElement.isConnected) {
        this.tryRecoverLostElement(currentTime);
        this.positionAnimationId = requestAnimationFrame(checkPosition);
        return;
      }

      if (currentTime - lastCheckTime < THROTTLE_MS) {
        this.positionAnimationId = requestAnimationFrame(checkPosition);
        return;
      }

      lastCheckTime = currentTime;

      const rect = this.currentElement.getBoundingClientRect();
      const currentPosition = {
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height
      };

      const currentVisibility = Finder.isElementVisible(this.currentElement);

      const positionChanged =
        this.lastPosition &&
        (Math.abs(currentPosition.x - this.lastPosition.x) > POSITION_THRESHOLD ||
          Math.abs(currentPosition.y - this.lastPosition.y) > POSITION_THRESHOLD ||
          Math.abs(currentPosition.width - this.lastPosition.width) > POSITION_THRESHOLD ||
          Math.abs(currentPosition.height - this.lastPosition.height) > POSITION_THRESHOLD);

      const visibilityChanged = this.lastVisibility !== currentVisibility;

      if (positionChanged || visibilityChanged) {
        this.sendPositionChanged();
      }

      this.lastPosition = currentPosition;
      this.lastVisibility = currentVisibility;
      this.positionAnimationId = requestAnimationFrame(checkPosition);
    };

    this.positionAnimationId = requestAnimationFrame(checkPosition);
  };

  private sendPositionChanged = async () => {
    window.parent.postMessage(
      {
        type: MessageTypes.POSITION_CHANGED
      },
      '*'
    );
  };

  private sendElementLost = async () => {
    window.parent.postMessage(
      {
        type: MessageTypes.ELEMENT_LOST
      },
      '*'
    );
  };

  private setupListeners(): void {
    window.addEventListener('message', async event => {
      if (event.data.type === MessageTypes.CHECK_RUNNING_GUIDE) {
        if (window.parent !== window) {
          window.parent.postMessage(
            {
              type: MessageTypes.CHECK_RUNNING_GUIDE
            },
            '*'
          );
        }
      }
      if (event.data.type === MessageTypes.GET_IFRAME_ELEMENT_DIMENSIONS) {
        this.source = event.source;
        this.targetOrigin = event.origin;

        const customPath = event.data.customPath;
        const pathsToUse = customPath
          ? [{ xpath: '', cssPath: [customPath], textXPath: null }]
          : event.data.paths;

        printGuideLog({
          message: '[Iframe] GET_IFRAME_ELEMENT_DIMENSIONS',
          data: {
            customPath: Boolean(customPath),
            pathSummary: summarizePathsForLog(pathsToUse)
          }
        });

        const dimensions = await Finder.getOverallElementDimensionsByPath(
          pathsToUse,
          {
            onBeforeFind: () => {
              if (this.currentElement) {
                this.stopPositionMonitoring();
              }
            },
            onElementFound: (element: HTMLElement) => {
              this.currentElement = element;
              this.startPositionMonitoring();
            },
            onElementLost: this.sendElementLost
          },
          event.data.nextStepButtonType
        );

        this.paths = pathsToUse;
        this.currentElement = dimensions.element;
        this.frameElement = dimensions.frameElement;

        printGuideLog({
          message: '[Iframe] SET_IFRAME_ELEMENT_DIMENSIONS (outgoing)',
          data: {
            x: dimensions.x,
            y: dimensions.y,
            width: dimensions.width,
            height: dimensions.height,
            isElementVisible: dimensions.isElementVisible,
            hasElement: Boolean(dimensions.element)
          }
        });

        window.parent.postMessage(
          {
            type: MessageTypes.SET_IFRAME_ELEMENT_DIMENSIONS,
            dimensions: {
              x: dimensions.x,
              y: dimensions.y,
              width: dimensions.width,
              height: dimensions.height,
              isElementVisible: dimensions.isElementVisible
            }
          },
          '*'
        );
      }
      if (event.data.type === MessageTypes.GET_IFRAME_ELEMENT_DIMENSIONS_BY_ELEMENT) {
        printGuideLog({
          message: '[Iframe] GET_IFRAME_ELEMENT_DIMENSIONS_BY_ELEMENT',
          data: {
            hasCurrentElement: Boolean(this.currentElement),
            pathSummary: summarizePathsForLog(event.data.paths)
          }
        });
        const dimensions = await Finder.getOverallElementDimensionsByElement(
          this.currentElement as HTMLElement,
          event.data.paths
        );
        if (!dimensions) {
          printGuideLog({
            message: '[Iframe] SET_IFRAME_ELEMENT_DIMENSIONS_BY_ELEMENT null',
            level: 'warn'
          });
          window.parent.postMessage(
            {
              type: MessageTypes.SET_IFRAME_ELEMENT_DIMENSIONS_BY_ELEMENT,
              dimensions: null
            },
            '*'
          );
        } else {
          window.parent.postMessage(
            {
              type: MessageTypes.SET_IFRAME_ELEMENT_DIMENSIONS_BY_ELEMENT,
              dimensions: {
                x: dimensions.x,
                y: dimensions.y,
                width: dimensions.width,
                height: dimensions.height,
                isOutOfViewport: dimensions.isOutOfViewport,
                isElementVisible: dimensions.isElementVisible
              }
            },
            '*'
          );
        }
      }
      if (event.data.type === MessageTypes.POSITION_CHANGED) {
        this.sendPositionChanged();
      }
      if (event.data.type === MessageTypes.ELEMENT_LOST) {
        this.sendElementLost();
      }
      if (event.data.type === MessageTypes.SCROLL_TO_ELEMENT) {
        if (this.currentElement) {
          scrollElementIntoViewForGuide(this.currentElement, 'smooth');
        }
        this.frameElement?.contentWindow?.postMessage(
          {
            type: MessageTypes.SCROLL_TO_ELEMENT
          },
          '*'
        );
      }
      if (event.data.type === MessageTypes.GO_TO_NEXT_STEP) {
        window.parent.postMessage({
          type: MessageTypes.GO_TO_NEXT_STEP
        });
      }
      if (event.data.type === MessageTypes.GO_TO_NEXT_STEP_SUCCESS) {
        const { frameElement } = Finder.splitPathsByIframe(this.paths);
        if (frameElement) {
          frameElement.contentWindow?.postMessage(
            {
              type: MessageTypes.GO_TO_NEXT_STEP_SUCCESS
            },
            '*'
          );
        } else {
          ClickCapture.getInstance().dispatchClick(this.clickedElement as HTMLElement);
        }
      }
    });
  }
}

export default Iframe;
